// Setup > Data — where this practice's claim data comes from, when it last arrived,
// and what (if anything) a biller can send.
//
// Every practice has this page, including one Atlas has no connector for: "nothing is
// connected yet, here is the file we can read" is the answer that practice needs, and
// it is the one they could not get before. The backend decides which of the three
// shapes applies (`upload.mode`) and this renders it — the page never reads
// emr_system / pm_system to decide anything, which is the whole point of the gate.
//
// Three shapes:
//   api feed   last sync, no upload box (a hand-made file landing in a ledger the next
//              sync owns would produce rows the sync will not reconcile).
//   file feed  the practice's own export, posted to the SAME /claims/import-file the
//              Claims page uses.
//   template   Atlas has no connector: download the Atlas layout, send it back.

import React, { useCallback, useEffect, useRef, useState } from "react";

import "./DataPage.css";
import AdvancedmdReportCard from "./AdvancedmdReportCard";
import EcwAppointmentsCard from "./EcwAppointmentsCard";
import EcwReportCard from "./EcwReportCard";
import EcwStatusCodesCard from "./EcwStatusCodesCard";
import PmTieOutCard from "./PmTieOutCard";
import PsDemographicsCard from "./PsDemographicsCard";
import SourceCoverageTable from "./SourceCoverageTable";

const API_URL = process.env.REACT_APP_API_URL || "";

// Poll while an import is running, matching the Claims Ledger chip's cadence.
// Exported so the test waits on THIS number rather than a copy that can drift from it.
export const POLL_MS = 4000;

const STATUS_LABELS = {
  running: "Import running",
  stalled: "Import stalled",
  error: "Last import failed",
  success: "Up to date",
  // The last import worked but is over 24 hours old; every feed should land daily.
  stale: "No import in 24 hours",
  idle: "Nothing imported yet",
};

// Badge colour per import state, using the app's existing status-badge vocabulary.
const STATUS_TONE = {
  running: "amber",
  stalled: "red",
  error: "red",
  success: "green",
  stale: "red",
  idle: "grey",
};

// A UTC timestamp from the API rendered in the reader's own timezone. Returns the em
// dash for a missing one rather than "Invalid Date".
export function formatWhen(iso) {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
}

// "3 hours ago" for the last import, so a biller can see staleness without doing
// arithmetic. Deliberately coarse: the exact time is beside it.
export function formatAge(iso, now = Date.now()) {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  const minutes = Math.floor((now - parsed.getTime()) / 60000);
  if (minutes < 0) return "just now";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// WHAT `stale_after_minutes` IS, AND IS NOT. It is the import pipeline's crashed-worker
// threshold: a run still flagged as started after that long is assumed dead, which is how
// the server derives `stalled`. It is NOT a freshness SLA for the practice's data, and an
// earlier draft of this page used it as one — it put "Older than 30 minutes" beside a
// nightly Cobalt sync that had last run nine hours ago and was working perfectly. So the
// number appears in one place only, explaining the `stalled` status, and the page states
// the age of the last import as a plain fact and lets the reader judge it.
export function stalledHint(data) {
  if (!data || !data.import || data.import.status !== "stalled") return null;
  return `No progress for over ${data.stale_after_minutes} minutes — the import probably stopped. Uploading again will start a new one.`;
}

// A name for the practice's system that is never blank. `source.label` is null whenever
// no source resolved — which includes a practice that HAS a converter and simply has not
// imported yet (the registry withholds a source key until there is evidence of a feed).
// Without the fallback the vendor card rendered "Send the export from ." at exactly that
// practice.
export function systemName(source) {
  return source.label || source.configured_system || null;
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || "grey";
  return (
    <span className={`data-badge data-badge-${tone}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// The automatic pulls Atlas makes for this practice (the PracticeSuite report from
// a mailbox or drop folder, the biller's working sheet for reason codes). `health`
// is the server's verdict against the job's daily cadence, in the same vocabulary
// feed health uses; "off" is a switch nobody has flipped yet.
const INGEST_HEALTH_LABELS = {
  fresh: "Running",
  stale: "Late",
  never: "Never run",
  error: "Last run failed",
  off: "Off",
};

const INGEST_HEALTH_TONE = {
  fresh: "green",
  stale: "amber",
  never: "grey",
  error: "red",
  off: "grey",
};

// "212 claims" for the report, "40 rows matched" for the sheet; the em dash when
// the job has not run.
export function ingestRowsText(entry) {
  if (
    !entry ||
    entry.rows_last_run === null ||
    entry.rows_last_run === undefined
  )
    return "—";
  const n = entry.rows_last_run;
  if (entry.kind === "working_sheet")
    return `${n} row${n === 1 ? "" : "s"} matched`;
  if (entry.kind === "appointments") return `${n} visit${n === 1 ? "" : "s"}`;
  return `${n} claim${n === 1 ? "" : "s"}`;
}

// The on/off for a feed the page can switch itself (the sheet-visits appointments
// feed today): PUT /api/practices/<id>/ingest-sources/<kind>, admin and above.
export function switchUrl(practiceId, kind) {
  return `${API_URL}/api/practices/${practiceId}/ingest-sources/${kind}`;
}

function IngestSources({ entries, practiceId, onSwitched }) {
  const [busyKind, setBusyKind] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  if (!entries || entries.length === 0) return null;
  const flip = async (entry) => {
    setBusyKind(entry.kind);
    setSwitchError(null);
    try {
      const response = await fetch(switchUrl(practiceId, entry.kind), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !entry.enabled }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error((body && body.error) || "Could not change the switch.");
      }
      if (onSwitched) onSwitched(body);
    } catch (err) {
      setSwitchError(err.message);
    } finally {
      setBusyKind(null);
    }
  };
  return (
    <div className="data-card">
      <h2>Automatic feeds</h2>
      <p className="data-note">
        What Atlas pulls for this practice on a schedule, and when it last did.
        Each feed has its own switch; one that is off is listed so nobody
        wonders whether it exists.
      </p>
      {switchError && (
        <div className="data-message-error" data-testid="data-switch-error">
          {switchError}
          <button type="button" className="data-link-button" aria-label="Dismiss" onClick={() => setSwitchError(null)}>
            ×
          </button>
        </div>
      )}
      <table className="data-table data-ingest-table">
        <thead>
          <tr>
            <th>Feed</th>
            <th>Supplies</th>
            <th>Status</th>
            <th>Last run</th>
            <th>Rows</th>
            <th>Switch</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const age = formatAge(entry.last_run_at);
            const tone = INGEST_HEALTH_TONE[entry.health] || "grey";
            return (
              <tr key={entry.kind} data-testid={`data-ingest-row-${entry.kind}`}>
                <td>{entry.label}</td>
                <td>{(entry.supplies || []).join(", ")}</td>
                <td>
                  <span className={`data-badge data-badge-${tone}`}>
                    {INGEST_HEALTH_LABELS[entry.health] || entry.health}
                  </span>
                </td>
                <td>
                  {formatWhen(entry.last_run_at)}
                  {age && <span className="data-age"> ({age})</span>}
                </td>
                <td>{ingestRowsText(entry)}</td>
                <td>
                  {entry.can_switch ? (
                    <button
                      type="button"
                      className="data-button data-button-secondary"
                      data-testid={`data-ingest-switch-${entry.kind}`}
                      disabled={busyKind === entry.kind}
                      onClick={() => flip(entry)}
                    >
                      {entry.enabled ? "Turn off" : "Turn on"}
                    </button>
                  ) : (
                    <span className="data-note">by request</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {entries.map((entry) => (
        <IngestDetail key={`${entry.kind}-detail`} entry={entry} />
      ))}
    </div>
  );
}

// The last run's own words, per feed. The sheet lists its unmatched rows by the
// sheet's Claim# and date of service — the two things a biller needs to find the
// row — and never a name; the server builds the list that way.
function IngestDetail({ entry }) {
  const result = entry.result || {};
  if (entry.last_status === "failed") {
    return (
      <p className="data-error-line">
        {entry.label}: the last run failed
        {result.message
          ? ` — ${result.message}`
          : result.error
            ? ` (${result.error})`
            : ""}
        .
      </p>
    );
  }
  if (entry.last_status === "skipped" && result.reason) {
    return (
      <p className="data-note">
        {entry.label}: nothing imported on the last run (
        {result.reason.replace(/_/g, " ")}).
      </p>
    );
  }
  if (entry.kind !== "working_sheet" || entry.last_status !== "ok") return null;
  const unmatched = result.unmatched || [];
  const count = result.unmatched_count || unmatched.length;
  const noClaim = unmatched.filter((row) => row.reason === "no_claim");
  const ambiguous = unmatched.filter((row) => row.reason === "ambiguous");
  const noClaimCount = result.no_claim_count ?? noClaim.length;
  const ambiguousCount = result.ambiguous_count ?? ambiguous.length;
  const tabLine = result.tab ? (
    <p className="data-note" data-testid="data-ingest-tab">
      {entry.label}: read tab “{result.tab}”
      {result.tab_resolved_by === "gid"
        ? ` (by gid${result.tab_gid !== null && result.tab_gid !== undefined ? ` ${result.tab_gid}` : ""})`
        : result.tab_resolved_by === "pattern"
          ? " (the newest dated tab matching the practice's pattern)"
          : result.tab_resolved_by === "name"
            ? " (by name)"
            : ""}
      .
    </p>
  ) : null;
  if (count === 0) {
    return (
      <>
        {tabLine}
        <p className="data-note">{entry.label}: every row matched a claim.</p>
      </>
    );
  }
  const reportSince = result.report_last_ingested_at
    ? ` (PracticeSuite report not ingested since ${formatWhen(result.report_last_ingested_at)})`
    : " (no PracticeSuite report ingested yet)";
  return (
    <div className="data-ingest-unmatched">
      {tabLine}
      <p className="data-note">
        {entry.label}: {count} row{count === 1 ? "" : "s"} did not match a claim.
        {noClaimCount
          ? ` ${noClaimCount} ${noClaimCount === 1 ? "is" : "are"} in the biller's sheet, not in Atlas${reportSince}; retried on the next run.`
          : ""}
        {ambiguousCount
          ? ` ${ambiguousCount} ${ambiguousCount === 1 ? "is" : "are"} ambiguous (more than one claim fits): a person picks; nothing was attached.`
          : ""}
        {count > unmatched.length
          ? ` The first ${unmatched.length} are listed.`
          : ""}
      </p>
      <table className="data-table">
        <thead>
          <tr>
            <th>Claim#</th>
            <th>DOS</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {unmatched.map((row) => (
            <tr key={`${row.claim_ref || "-"}-${row.dos}-${row.line}`}>
              <td>{row.claim_ref || "—"}</td>
              <td>{row.dos}</td>
              <td>
                {row.reason === "ambiguous"
                  ? `${row.detail || "more than one claim matches"}${
                      row.candidates && row.candidates.length
                        ? `: claims ${row.candidates.map((id) => `#${id}`).join(", ")}`
                        : ""
                    }`
                  : "in the biller's sheet, not in Atlas"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TemplateColumns({ columns }) {
  const [open, setOpen] = useState(false);
  if (!columns || columns.length === 0) return null;
  return (
    <div className="data-columns">
      <button
        type="button"
        className="data-link-button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide" : "Show"} the {columns.length} columns
      </button>
      {open && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Column</th>
              <th>Required</th>
              <th>What goes in it</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column) => (
              <tr key={column.name}>
                <td>
                  <code>{column.name}</code>
                </td>
                <td>{column.required ? "Required" : "Optional"}</td>
                <td>{column.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function DataPage({ selectedPractice, embedded = false }) {
  const practiceId = selectedPractice && selectedPractice.id;
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [message, setMessage] = useState(null);
  const [staleConfirm, setStaleConfirm] = useState(null);
  // The counts belong to an upload done HERE, in this session. A practice switch makes
  // them somebody else's numbers again, so they are cleared with everything else.
  const settledRef = useRef(false);

  const load = useCallback(async () => {
    if (!practiceId) return null;
    const response = await fetch(
      `${API_URL}/api/practices/${practiceId}/data`,
      {
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error("Could not load this practice's data settings.");
    }
    return response.json();
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setFile(null);
    setMessage(null);
    setUploadError(null);
    setStaleConfirm(null);
    settledRef.current = false;
    load()
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  // While an import is running, poll until it settles, then say how it went. Only
  // mounted in the running state, so a settled page issues no requests at all.
  const running = data && data.import && data.import.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      load()
        .then((body) => {
          if (!body) return;
          setData(body);
          if (body.import.status !== "running" && !settledRef.current) {
            settledRef.current = true;
            if (body.import.status === "success") {
              setMessage({
                kind: "success",
                text: `Import finished — ${body.import.created} created, ${body.import.updated} updated.`,
              });
            } else if (body.import.error) {
              setMessage({ kind: "error", text: body.import.error });
            }
          }
        })
        .catch(() => {
          /* a dropped poll is not an answer; the next one tries again */
        });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [running, load]);

  const downloadTemplate = async () => {
    setMessage(null);
    try {
      // A fetch rather than an <a href>: Atlas authenticates /api/* with the Clerk
      // token authFetch.js attaches, which a plain link cannot carry.
      const response = await fetch(`${API_URL}${data.template.url}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(
          `Could not download the template (${response.status}).`,
        );
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = "atlas-claims-template.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    }
  };

  const submit = async (chosen, confirmStale = false) => {
    setBusy(true);
    setUploadError(null);
    setMessage(null);
    setStaleConfirm(null);
    const body = new FormData();
    body.append("file", chosen);
    if (confirmStale) body.append("confirm_stale", "true");
    try {
      const response = await fetch(`${API_URL}${data.upload.url}`, {
        method: "POST",
        credentials: "include",
        body,
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 202) {
        setFile(null);
        settledRef.current = false;
        setData((prev) => ({
          ...prev,
          import: { ...prev.import, status: "running", error: null },
        }));
        setMessage({
          kind: "success",
          text: `Accepted — ${payload.rows} row${payload.rows === 1 ? "" : "s"} are being imported.`,
        });
      } else if (response.status === 409 && payload.stale) {
        setStaleConfirm({ file: chosen, ...payload });
      } else if (response.ok) {
        // A 200 is the pipeline's no-op: nothing in the file to import.
        setFile(null);
        setMessage({
          kind: "success",
          text: "That file had no new rows to import.",
        });
      } else {
        setUploadError(payload.error || "The upload failed.");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!practiceId) return null;

  if (loadError) {
    return (
      <div className="data-page">
        <div className="data-card data-card-error">
          <h2>Couldn't load this practice's data settings</h2>
          <p>{loadError} Reload the page to try again.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const { source, upload, template } = data;
  const status = data.import.status;
  const age = formatAge(data.import.last_imported_at);
  const stalled = stalledHint(data);
  const apiFed = data.delivery === "api";

  // `embedded`: inside Practice Details' "Uploads & feeds" modal (2026-10-25). The Data
  // table there already shows the source, freshness and coverage, so only the cards a
  // person works with render here — plus the import card when something went wrong.
  const importNeedsEyes = status === "running" || status === "error" || !!stalled || !!data.import.error;

  return (
    <div className={`data-page${embedded ? " data-page-embedded" : ""}`}>
      {!embedded && (
        <div className="data-header">
          <h1>Data</h1>
          <p className="data-subtitle">
            Where {selectedPractice.name}&rsquo;s claim data comes from, and when
            it last arrived.
          </p>
        </div>
      )}

      {message && (
        <div className={`data-message data-message-${message.kind}`}>
          <span>{message.text}</span>
          <button
            type="button"
            className="data-message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      {!embedded && (
      <div className="data-card">
        <h2>Source</h2>
        <dl className="data-facts">
          <dt>System</dt>
          <dd>{systemName(source) || "Nothing connected"}</dd>
          <dt>How it arrives</dt>
          <dd>{data.delivery_label}</dd>
        </dl>
        <p className="data-note">{data.delivery_description}</p>
        <p className="data-reason">{source.reason}</p>
      </div>
      )}

      {(!embedded || importNeedsEyes) && (
      <div className="data-card">
        <h2>{apiFed ? "Last sync" : "Last import"}</h2>
        <div className="data-status-row">
          <StatusBadge status={status} />
        </div>
        <dl className="data-facts">
          <dt>{apiFed ? "Last sync" : "Last import"}</dt>
          <dd>
            {formatWhen(data.import.last_imported_at)}
            {age && <span className="data-age"> ({age})</span>}
          </dd>
          {status === "running" && (
            <>
              <dt>Started</dt>
              <dd>{formatWhen(data.import.import_started_at)}</dd>
            </>
          )}
          <dt>Claims created</dt>
          <dd>{data.import.created}</dd>
          <dt>Claims updated</dt>
          <dd>{data.import.updated}</dd>
        </dl>
        {stalled && <p className="data-error-line">{stalled}</p>}
        {data.import.error && (
          <p className="data-error-line">{data.import.error}</p>
        )}
      </div>
      )}

      {/* What is covered, and by what: every data item, its source here, its
          freshness and whether it is live, in flight, needs a pull or is blocked.
          Embedded, the Data table beside the modal is this. */}
      {!embedded && <SourceCoverageTable practiceId={practiceId} />}

      {/* The PM's aging report against Atlas's open claims, at two bases (Jordan's
          PM tie-out, 2026-10-20). Its own card, its own requests. */}
      <PmTieOutCard practiceId={practiceId} />

      {/* eCW's weekly claims report, uploaded beside the Cobalt feed (2026-10-20).
          Its own card, its own requests; absent unless the practice bills from eCW. */}
      <EcwReportCard practiceId={practiceId} />
      <EcwAppointmentsCard practiceId={practiceId} />

      {/* PracticeSuite's patient demographics export, uploaded for patient outreach
          (2026-10-23). Its own card, its own requests; absent unless the practice
          bills from PracticeSuite. */}
      <PsDemographicsCard practiceId={practiceId} />

      {/* The eCW status codes on this practice's Cobalt claims that have no meaning
          yet, with claim counts (2026-10-21); the meanings are set on Practice Rules.
          Absent unless the practice has Cobalt claims. */}
      {/* Embedded: the meanings are edited on Practice Rules, one tab over. */}
      {!embedded && <EcwStatusCodesCard practiceId={practiceId} />}

      {/* AdvancedMD's reports, uploaded by hand in a fixed order (2026-10-21). Its own
          card, its own requests; absent unless the practice bills from AdvancedMD. */}
      <AdvancedmdReportCard practiceId={practiceId} />

      {/* The scheduled pulls Atlas makes for this practice (docs/vortex-sources.md):
          their switches, last run, rows, and the sheet's unmatched rows. */}
      <IngestSources
        entries={data.ingest}
        practiceId={practiceId}
        onSwitched={(entry) =>
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  ingest: (prev.ingest || []).map((e) => (e.kind === entry.kind ? { ...e, ...entry } : e)),
                }
              : prev,
          )
        }
      />

      {upload.mode === "none" && (
        <div className="data-card">
          <h2>Uploading</h2>
          {/* The server's sentence, not a second copy of it here: it is the one that
              knows whether this practice also has a converter behind the Claims page.
              A hardcoded "nothing to upload" was false for every eCW practice. */}
          <p className="data-note">{data.delivery_description}</p>
        </div>
      )}

      {upload.mode === "vendor" && (
        <div className="data-card">
          <h2>Upload an export</h2>
          <p className="data-note">
            {systemName(source)
              ? `Send the export from ${systemName(source)}.`
              : "Send the export from this practice's system."}{" "}
            Atlas reads it under the same rules the Claims page uses.
          </p>
          {upload.enabled ? (
            <UploadBox
              file={file}
              setFile={setFile}
              busy={busy}
              error={uploadError}
              staleConfirm={staleConfirm}
              onSubmit={submit}
              onCancelStale={() => setStaleConfirm(null)}
            />
          ) : (
            <p className="data-error-line">{upload.blocked_reason}</p>
          )}
        </div>
      )}

      {upload.mode === "template" && (
        <div className="data-card">
          {/* The heading follows `no_connector` (the SOURCE), not the upload mode. The
              demo practice is offered the template and has a source; telling a biller
              Atlas has no connector for a system it resolved by name is simply false. */}
          <h2>
            {data.no_connector
              ? "Atlas has no connector for this system yet"
              : "Send claims in the Atlas layout"}
          </h2>
          <p className="data-note">
            {data.no_connector
              ? "Atlas can\u2019t read this practice\u2019s system directly. Download the Atlas template, fill it in from whatever the practice can export, and upload it here \u2014 a file in that layout imports like any other feed."
              : "Download the Atlas template, fill it in, and upload it here \u2014 a file in that layout imports like any other feed."}
          </p>
          <button
            type="button"
            className="data-button data-button-secondary"
            onClick={downloadTemplate}
          >
            Download the template (CSV)
          </button>
          <TemplateColumns columns={template && template.columns} />
          <UploadBox
            file={file}
            setFile={setFile}
            busy={busy}
            error={uploadError}
            staleConfirm={staleConfirm}
            onSubmit={submit}
            onCancelStale={() => setStaleConfirm(null)}
          />
        </div>
      )}
    </div>
  );
}

function UploadBox({
  file,
  setFile,
  busy,
  error,
  staleConfirm,
  onSubmit,
  onCancelStale,
}) {
  return (
    <div className="data-upload">
      <label className="data-file-label">
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const chosen = e.target.files[0];
            if (chosen) setFile(chosen);
            // Clear the input so re-picking the SAME file still fires onChange.
            e.target.value = "";
          }}
        />
      </label>
      {file && <p className="data-filename">{file.name}</p>}
      <button
        type="button"
        className="data-button"
        disabled={!file || busy}
        onClick={() => onSubmit(file)}
      >
        {busy ? "Uploading…" : "Upload"}
      </button>
      {error && <p className="data-error-line">{error}</p>}
      {staleConfirm && (
        <div className="data-stale">
          <p>{staleConfirm.error}</p>
          <p className="data-note">
            This file goes to {staleConfirm.file_through}; Atlas already has
            data through {staleConfirm.imported_through}.
          </p>
          <button
            type="button"
            className="data-button"
            disabled={busy}
            onClick={() => onSubmit(staleConfirm.file, true)}
          >
            Import anyway
          </button>
          <button
            type="button"
            className="data-button data-button-secondary"
            onClick={onCancelStale}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
